import { useLayoutEffect, useState } from "react";

import type { SpecDetailsSearch } from "@/api/contracts/specifications";
import { ItemDetailDialog, type ItemDialogSeed } from "@/components/catalog/item-detail-dialog";
import { usePermissions } from "@/hooks/permissions";
import { useTargetCostSwitch } from "@/hooks/target-cost-switch";
import {
  useSpecErrorSnackbar,
  useSpecGroupExpansion,
  useSpecLinesLoader,
} from "@/hooks/spec-lines";
import { LOAD_ERROR, SPEC_PAGE_TITLE } from "@/lib/spec-display";
import { deepLinkId, offersChangesSwitch, offersTargetCostSwitch } from "@/lib/spec-lines-display";

import {
  SpecDetailsFrame,
  SpecDetailsIslandSkeleton,
  type SpecDetailsLoaded,
} from "./spec-details-frame";
import { SpecLinesBody } from "./spec-lines-body";
import { SpecLinesToolbar } from "./spec-lines-toolbar";
import { TargetCostNotification } from "./target-cost-notification";

// One version's details page, whole — the donor's `SpecDetailsIsland` and the
// read-only item dialog its page mounts beside it (`pages/spec-details-page.tsx`)
// (#694, #695). The route hands it the summary, the version and the URL's
// search params, and writes the line search back; everything else is here: the
// lines loader, the expansion map, the "Show changes from Base" state and the
// dialog's item.
//
// Until the first lines page of the page view lands, the whole island is the
// donor's `IslandSkeleton` — the header's bars and five groups — as the donor
// returns it for `isLoadingMetadata || isLoadingLines` (`SpecDetailsIsland
// .tsx:149`). A later term's first page is the body's search skeleton instead.
//
// **Target cost** (#1216): the switch drives the visit's one target-cost
// setting (`useTargetCostSwitch("targetCost")`), which a community spec's
// editor shares (#1241 D1), session-wide and on by default. The switch is offered to an editor on a
// community spec; the Notes widening follows the state for every viewer, as
// the donor's `GroupRow` reads the slice whoever is looking; and the toast is
// mounted, past the island skeleton, while the switch is offered and on
// (`SpecDetailsIsland.tsx:141-147`), so every arrival and every switch-on
// raises it afresh.

/**
 * What the route read for the page: the summary and the URL's version on it,
 * or — the read having **failed** — nothing but the URL's two ids. A read that
 * succeeded on absence never gets here; the loader answers it with Page not
 * found.
 */
export type SpecDetailsRead =
  SpecDetailsLoaded | { summary: null; specId: number; versionId: number };

interface SpecDetailsPageProps {
  read: SpecDetailsRead;
  search: SpecDetailsSearch;
  /** Writes `?specSearch=`; `""` removes it. */
  onSearchChange: (term: string) => void;
}

/**
 * The route's pending face: the island skeleton, and the tab's
 * `Specification` from its first frame (#554 D4 (iii)) — the donor's `Layout`
 * sets it before any data arrives (`Layout.tsx:19-24`, less the suffix). The
 * route's head cannot: the router projects heads only once every loader has
 * settled, so without this the pending frames keep the root's title. The head
 * takes the title back as the loader settles.
 */
export function SpecDetailsPending() {
  useLayoutEffect(() => {
    document.title = SPEC_PAGE_TITLE;
  }, []);
  return <SpecDetailsIslandSkeleton />;
}

export function SpecDetailsPage({ read, search, onSearchChange }: SpecDetailsPageProps) {
  const permissions = usePermissions();
  const urlSearch = search.specSearch ?? "";
  const { summary } = read;
  // The lines are read by the URL's ids alone, as the donor's are
  // (`shared/data/loader.ts:43-47`), so they load under a failed summary read.
  const specId = read.summary ? read.summary.id : read.specId;
  const versionId = read.summary ? read.version.id : read.versionId;
  const loader = useSpecLinesLoader(specId, versionId, urlSearch);
  // One snackbar for either read, as the donor raises one on `isErrorMetadata
  // || isErrorLines` (`SpecDetailsIsland.tsx:289-291`) — and, as the donor's,
  // past the loading gate: its island returns the skeleton first (`:149`), so
  // nothing is raised until the first lines page has settled.
  useSpecErrorSnackbar(
    !loader.isInitialLoading && (summary === null || loader.isError),
    LOAD_ERROR,
  );
  const expansion = useSpecGroupExpansion(loader.ordering?.map((entry) => entry.group.id));
  // "Show changes from Base": component state, off on arrival, never kept.
  const [showChanges, setShowChanges] = useState(false);
  const [openItem, setOpenItem] = useState<ItemDialogSeed | null>(null);
  const [showTargetCost, setShowTargetCost] = useTargetCostSwitch("targetCost");
  const offersTargetCost =
    summary !== null && offersTargetCostSwitch(summary, permissions.canEditSpecs);

  if (loader.isInitialLoading) return <SpecDetailsIslandSkeleton />;

  return (
    <>
      <SpecDetailsFrame
        read={read.summary ? read : null}
        toolbar={
          <SpecLinesToolbar
            urlSearch={urlSearch}
            onSearch={onSearchChange}
            isSearching={loader.isSearching}
            isEmpty={loader.isEmpty}
            expansion={expansion}
            // Neither switch without a summary: the donor's want a based-on id
            // and a community type (`SwitchChanges.tsx:22-25`,
            // `SpecDetailsIsland.tsx:55-56`).
            showChangesSwitch={summary !== null && offersChangesSwitch(summary)}
            showChanges={showChanges}
            onShowChangesChange={setShowChanges}
            showTargetCostSwitch={offersTargetCost}
            showTargetCost={showTargetCost}
            onShowTargetCostChange={setShowTargetCost}
          />
        }
      >
        <SpecLinesBody
          loader={loader}
          expansion={expansion}
          // The donor's blank type is not a community one (`LineDetails.tsx:19-22`);
          // it only names a change, and the changes switch is not offered.
          specType={summary?.type ?? "core"}
          showChanges={showChanges}
          showTargetCost={showTargetCost}
          lineId={deepLinkId(search.lineId)}
          selectionId={deepLinkId(search.selectionId)}
          onOpenItem={setOpenItem}
        />
      </SpecDetailsFrame>
      <ItemDetailDialog item={openItem} onClose={() => setOpenItem(null)} />
      {offersTargetCost && showTargetCost ? (
        <TargetCostNotification count={summary.overTargetCount} />
      ) : null}
    </>
  );
}
